import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import en from "./en.json";
import hi from "./hi.json";
import { API_BASE } from "../utils/api";

const BUILT_IN = { en, hi };
const LanguageContext = createContext(null);

function getNested(obj, path) {
  return path.split(".").reduce((acc, k) => (acc && acc[k] != null ? acc[k] : null), obj);
}

function setNested(obj, path, value) {
  const parts = path.split(".");
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    if (typeof cur[parts[i]] !== "object" || cur[parts[i]] == null) cur[parts[i]] = {};
    cur = cur[parts[i]];
  }
  cur[parts[parts.length - 1]] = value;
}

// Deep-clone JSON dict (small objects, fine to JSON-roundtrip)
const cloneDict = (d) => JSON.parse(JSON.stringify(d));

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => localStorage.getItem("lang") || "en");
  const [dicts, setDicts] = useState({ en: cloneDict(en), hi: cloneDict(hi) });

  // Fetch overrides at boot, merge over built-in JSON
  useEffect(() => {
    fetch(`${API_BASE}/api/translations`)
      .then((r) => (r.ok ? r.json() : []))
      .then((overrides) => {
        if (!Array.isArray(overrides) || overrides.length === 0) return;
        const next = { en: cloneDict(en), hi: cloneDict(hi) };
        for (const o of overrides) {
          if (next[o.lang]) setNested(next[o.lang], o.key, o.value);
        }
        setDicts(next);
      })
      .catch(() => {});
  }, []);

  const setLang = useCallback((next) => {
    localStorage.setItem("lang", next);
    setLangState(next);
  }, []);

  const t = useCallback(
    (key, fallback) => {
      const dict = dicts[lang] || dicts.en;
      const val = getNested(dict, key);
      if (val != null) return val;
      if (lang !== "en") {
        const fb = getNested(dicts.en, key);
        if (fb != null) return fb;
      }
      return fallback != null ? fallback : key;
    },
    [lang, dicts]
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, builtIn: BUILT_IN, dicts }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useT = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    // Safety fallback so components don't crash if used outside the provider
    return { lang: "en", setLang: () => {}, t: (k, fb) => fb || k };
  }
  return ctx;
};

export const LanguageToggle = ({ className = "" }) => {
  const { lang, setLang } = useT();
  return (
    <button
      onClick={() => setLang(lang === "en" ? "hi" : "en")}
      title="Toggle language"
      className={`inline-flex items-center justify-center bg-gray-700 hover:bg-gray-600 text-white rounded text-[10px] font-semibold leading-none w-8 h-6 ${className}`}
    >
      {lang === "en" ? "EN" : "हिं"}
    </button>
  );
};
